import { PaymentWebsocket } from '@cibli/shared';

import { WS_ORIGIN } from '@/lib/api-url';

export const WS_URL = WS_ORIGIN;

type EventHandler = (data: PaymentWebsocket.PaymentWebSocketMessage) => void;
type EventType =
  'reader_state_update' | 'payment_intent_update' | 'subscription_response' | 'subscription_update';

class PaymentWebSocketService {
  private ws: WebSocket | null = null;
  private eventHandlers: Map<EventType, Set<EventHandler>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private subscribedReaders = new Set<string>();
  private subscribedPaymentIntents = new Set<string>();
  // Pending subscription promises: paymentIntentId -> { resolve, reject, timeout }
  private pendingPaymentIntentSubscriptions = new Map<
    string,
    {
      resolve: () => void;
      reject: (error: Error) => void;
      timeout: NodeJS.Timeout;
    }
  >();

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    try {
      this.ws = new WebSocket(`${WS_URL}/payment-events`);
      this.setupEventListeners();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.reconnectAttempts = 0;
    this.subscribedReaders.clear();
    this.subscribedPaymentIntents.clear();
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  on(event: EventType, handler: EventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: EventType, handler: EventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Subscribe to updates from a specific reader
   * @param readerId The ID of the reader to subscribe to
   */
  subscribeToReader(readerId: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.subscribedReaders.add(readerId);
      this.ws.send(
        JSON.stringify({
          type: 'subscribe_to_reader',
          readerId,
        } as PaymentWebsocket.SubscribeToReaderRequest),
      );
    } else {
      // If not connected, add to subscription list for when we reconnect
      this.subscribedReaders.add(readerId);
    }
  }

  /**
   * Unsubscribe from updates from a specific reader
   * @param readerId The ID of the reader to unsubscribe from
   */
  unsubscribeFromReader(readerId: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.subscribedReaders.delete(readerId);
      this.ws.send(
        JSON.stringify({
          type: 'unsubscribe_from_reader',
          readerId,
        } as PaymentWebsocket.UnsubscribeFromReaderRequest),
      );
    }
  }

  /**
   * Get the list of currently subscribed readers
   * @returns Array of reader IDs that this client is subscribed to
   */
  getSubscribedReaders(): string[] {
    return Array.from(this.subscribedReaders);
  }

  /**
   * Check if subscribed to a specific reader
   * @param readerId The reader ID to check
   * @returns True if subscribed to the reader
   */
  isSubscribedToReader(readerId: string): boolean {
    return this.subscribedReaders.has(readerId);
  }

  /**
   * Subscribe to updates from a specific payment intent
   * Returns a Promise that resolves when the subscription is confirmed by the server
   * @param paymentIntentId The ID of the payment intent to subscribe to
   * @param timeoutMs Timeout in milliseconds (default: 2000ms)
   * @returns Promise that resolves when subscription is confirmed
   */
  subscribeToPaymentIntent(paymentIntentId: string, timeoutMs: number = 2000): Promise<void> {
    // If already subscribed, resolve immediately
    if (this.subscribedPaymentIntents.has(paymentIntentId)) {
      return Promise.resolve();
    }

    // If there's already a pending subscription, return that promise
    const existingPending = this.pendingPaymentIntentSubscriptions.get(paymentIntentId);
    if (existingPending) {
      return new Promise((resolve, reject) => {
        const originalResolve = existingPending.resolve;
        const originalReject = existingPending.reject;
        existingPending.resolve = () => {
          originalResolve();
          resolve();
        };
        existingPending.reject = (error: Error) => {
          originalReject(error);
          reject(error);
        };
      });
    }

    return new Promise((resolve, reject) => {
      // Set up timeout
      const timeout = setTimeout(() => {
        this.pendingPaymentIntentSubscriptions.delete(paymentIntentId);
        reject(
          new Error(
            `Subscription to payment intent ${paymentIntentId} timed out after ${timeoutMs}ms`,
          ),
        );
      }, timeoutMs);

      // Store the promise handlers
      this.pendingPaymentIntentSubscriptions.set(paymentIntentId, {
        resolve: () => {
          clearTimeout(timeout);
          this.pendingPaymentIntentSubscriptions.delete(paymentIntentId);
          resolve();
        },
        reject: (error: Error) => {
          clearTimeout(timeout);
          this.pendingPaymentIntentSubscriptions.delete(paymentIntentId);
          reject(error);
        },
        timeout,
      });

      // Send subscription request
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.subscribedPaymentIntents.add(paymentIntentId);
        this.ws.send(
          JSON.stringify({
            type: 'subscribe_to_payment_intent',
            paymentIntentId,
          }),
        );
      } else {
        // If not connected, add to subscription list for when we reconnect
        this.subscribedPaymentIntents.add(paymentIntentId);
        // Reject if not connected (will be retried on reconnect)
        clearTimeout(timeout);
        this.pendingPaymentIntentSubscriptions.delete(paymentIntentId);
        reject(new Error('WebSocket is not connected'));
      }
    });
  }

  unsubscribeFromPaymentIntent(paymentIntentId: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.subscribedPaymentIntents.delete(paymentIntentId);
      this.ws.send(
        JSON.stringify({
          type: 'unsubscribe_from_payment_intent',
          paymentIntentId,
        }),
      );
    }
  }

  isSubscribedToPaymentIntent(paymentIntentId: string): boolean {
    return this.subscribedPaymentIntents.has(paymentIntentId);
  }

  /**
   * Get the list of currently subscribed payment intents
   * @returns Array of payment intent IDs that this client is subscribed to
   */
  getSubscribedPaymentIntents(): string[] {
    return Array.from(this.subscribedPaymentIntents);
  }

  private setupEventListeners(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('Payment WebSocket connected');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;

      // Resubscribe to previously subscribed readers after reconnection
      this.subscribedReaders.forEach(readerId => {
        this.subscribeToReader(readerId);
      });

      // Resubscribe to previously subscribed payment intents after reconnection
      this.subscribedPaymentIntents.forEach(paymentIntentId => {
        // Don't await, just resubscribe (promises will be handled separately)
        this.subscribeToPaymentIntent(paymentIntentId).catch(error => {
          console.error(`Failed to resubscribe to payment intent ${paymentIntentId}:`, error);
        });
      });
    };

    this.ws.onmessage = event => {
      try {
        const message = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onclose = event => {
      console.log('Payment WebSocket disconnected:', event.code, event.reason);
      this.scheduleReconnect();
    };

    this.ws.onerror = error => {
      console.error('Payment WebSocket error:', error);
    };
  }

  private handleMessage(webhookData: PaymentWebsocket.SocketData): void {
    const { type } = webhookData;

    switch (type) {
      // Terminal reader events include a data property with the reader update
      case 'terminal.reader.action_succeeded':
      case 'terminal.reader.action_failed':
      case 'terminal.reader.updated':
      case 'terminal.reader.started':
      case 'terminal.reader.canceled': {
        const readerUpdate = (webhookData as PaymentWebsocket.TerminalReaderEvent).data;
        this.emit('reader_state_update', readerUpdate);
        break;
      }

      // Payment intent update event wraps the intent state inside `data`
      case 'payment_intent_update': {
        const paymentUpdate = (webhookData as PaymentWebsocket.PaymentIntentStateUpdateEvent).data;
        this.emit('payment_intent_update', paymentUpdate);
        break;
      }

      // Subscription response & update are sent at the root level (no data property)
      case 'subscription_response': {
        const subscriptionResponse = webhookData as PaymentWebsocket.SubscriptionResponse;
        const responseData = subscriptionResponse.data;

        // Resolve pending payment intent subscription promises
        if (responseData.paymentIntentId) {
          const pending = this.pendingPaymentIntentSubscriptions.get(responseData.paymentIntentId);
          if (pending && responseData.success) {
            pending.resolve();
          } else if (pending) {
            pending.reject(
              new Error(
                responseData.message ||
                  `Failed to subscribe to payment intent ${responseData.paymentIntentId}`,
              ),
            );
          }
        }

        this.emit('subscription_response', responseData);
        break;
      }

      case 'subscription_update': {
        const subscriptionUpdate = webhookData as PaymentWebsocket.SubscriptionUpdate;
        this.emit('subscription_update', subscriptionUpdate.data);
        break;
      }

      default: {
        console.warn('Unhandled WebSocket message type:', type, webhookData);
      }
    }
  }

  private emit(event: EventType, data: PaymentWebsocket.PaymentWebSocketMessage): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000); // Max 30 seconds

    console.log(
      `Scheduling reconnection attempt ${this.reconnectAttempts} in ${this.reconnectDelay}ms`,
    );

    this.reconnectTimeout = setTimeout(() => {
      console.log(`Attempting to reconnect (attempt ${this.reconnectAttempts})`);
      this.connect();
    }, this.reconnectDelay);
  }
}

export const paymentWebSocket = new PaymentWebSocketService();
